import React from "react";
import type { Dispatch } from "react";
import type { SelectableChatBoxListState, SelectableChatBoxListAction } from "./types";
import { isFn } from "../../utils/is-what";
import { useDragSelect } from "./hooks/use-drag-select";
import { useChatBoxListUIController } from "../../contexts";

export const SelectableChatBoxListStateContext = React.createContext<SelectableChatBoxListState>(
	null as unknown as SelectableChatBoxListState,
);

export const SelectableChatBoxListDispatchContext = React.createContext<
	Dispatch<SelectableChatBoxListAction>
>(() => {});

export interface SelectableChatBoxListProps {
	children: React.ReactElement<any> | (() => React.ReactElement<any>);
}

const SelectableChatBoxListStateReducer = (
	state: SelectableChatBoxListState,
	action: SelectableChatBoxListAction,
) => {
	switch (action.type) {
		case "clear-all-selected-items":
			return { ...state, selectedItemsMap: {} };
		case "clear-selected-item":
			const { selectKey } = action.payload;
			const newSelectedItemsMap = { ...state.selectedItemsMap };
			delete newSelectedItemsMap[selectKey];
			return {
				...state,
				selectedItemsMap: newSelectedItemsMap,
			};
		case "toggle-selected-item":
			return {
				...state,
				selectedItemsMap: {
					...state.selectedItemsMap,
					[action.payload.selectKey]: !state.selectedItemsMap[action.payload.selectKey],
				},
			};
		case "overwrite-selected-items-map":
			return {
				...state,
				selectedItemsMap: action.payload.selectedItemsMap,
			};
		default:
			return state;
	}
};

export const SelectableChatBoxListProvider = React.memo((props: SelectableChatBoxListProps) => {
	const { chatBoxListId } = useChatBoxListUIController();
	const [state, dispatch] = React.useReducer(
		SelectableChatBoxListStateReducer,
		React.useMemo(() => ({ selectedItemsMap: {} }), []),
	);

	useDragSelect({ chatBoxListId, state, dispatch });

	return (
		<SelectableChatBoxListStateContext.Provider value={state}>
			<SelectableChatBoxListDispatchContext.Provider value={dispatch}>
				{(() => {
					if (isFn(props.children)) {
						return props.children();
					}
					return props.children;
				})()}
			</SelectableChatBoxListDispatchContext.Provider>
		</SelectableChatBoxListStateContext.Provider>
	);
});
